import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import GameCard from '../components/GameCard';
import GameForm from '../components/GameForm';
import { getCachedGames, setCachedGames, clearGamesCache } from '../utils/cache';
import { getGameTheme } from '../utils/gameThemes';
import SEO from '../components/SEO';
import {
  DEFAULT_SITE_META_DESCRIPTION,
  getSiteUrl,
  organizationJsonLd,
  websiteJsonLd,
} from '../constants/seo';
import { createChallengePath, slugForGame } from '../utils/gameSlug';
import HomeExploreSections from '../components/HomeExploreSections';
import ChallengeRoadmap from '../components/ChallengeRoadmap';
import Vortex from '../components/Vortex';
import TextGenerateEffect from '../components/TextGenerateEffect';
import ShimmerCtaButton from '../components/ShimmerCtaButton';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';
import './Home.css';

/** Hero tagline — only this line uses TextGenerateEffect in the hero. */
const HERO_ANIMATED_TAGLINE =
  'Challenge friends, lock crypto in a smart contract, and settle bets directly—no middlemen.';

export default function Home() {
  const prefersReducedMotion = usePrefersReducedMotion();
  const { gameSlug } = useParams();
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageSettings, setPageSettings] = useState({
    pageTitle: 'EWIO – Peer‑to‑Peer Skill‑Based Crypto Betting Platform',
    heroTitle: 'Peer‑to‑Peer Skill‑Based Crypto Betting',
    heroDescription: HERO_ANIMATED_TAGLINE,
  });
  const normalizedSlug = gameSlug ? String(gameSlug).toLowerCase() : null;

  // Find selected game by URL slug (numeric segment still supported: /create-challenge/game-5)
  const selectedGame =
    normalizedSlug && games.length > 0
      ? games.find((g) => slugForGame(g) === normalizedSlug)
      : null;
  const selectedGameId = selectedGame ? selectedGame.gameId : null;

  // Load page settings on page load
  useEffect(() => {
    api.get('/page-settings')
      .then(r => {
        if (r.data && typeof r.data === 'object') {
          setPageSettings({
            pageTitle: r.data.pageTitle || 'EWIO – Peer‑to‑Peer Skill‑Based Crypto Betting Platform',
            heroTitle: r.data.heroTitle || 'Peer‑to‑Peer Skill‑Based Crypto Betting',
            heroDescription: r.data.heroDescription || HERO_ANIMATED_TAGLINE,
          });
        }
      })
      .catch(err => {
        console.error('Failed to load page settings:', err.message);
        // Use defaults on error
      });
  }, []);

  // Bad slug or empty games on slug route → home
  useEffect(() => {
    if (!normalizedSlug || loading) return;
    if (games.length === 0 || !selectedGame) {
      navigate('/', { replace: true });
    }
  }, [normalizedSlug, loading, games, selectedGame, navigate]);

  // Load games on page load
  useEffect(() => {
    // Try to load from cache first
    const cachedGames = getCachedGames();
    if (cachedGames && Array.isArray(cachedGames) && cachedGames.length > 0) {
      setGames(cachedGames);
      setLoading(false);
      
      // Optionally refresh in background (stale-while-revalidate pattern)
      api.get('/games')
        .then(r => {
          if (Array.isArray(r.data) && r.data.length > 0) {
            setCachedGames(r.data);
            setGames(r.data);
          }
        })
        .catch(() => {
          // Silently fail, use cached data
        });
      return;
    }

    // No cache available, fetch from API
    setLoading(true);
    api.get('/games')
      .then(r => {
        // Check if response is HTML (API routing issue)
        if (typeof r.data === 'string' && r.data.trim().startsWith('<!')) {
          console.error('API returned HTML instead of JSON');
          alert('API routing error: Received HTML instead of JSON.');
          setGames([]);
          return;
        }
        
        const gamesData = Array.isArray(r.data) ? r.data : [];
        setGames(gamesData);
        
        // Cache the games data
        if (Array.isArray(r.data) && r.data.length > 0) {
          setCachedGames(gamesData);
        }
      })
      .catch(err => {
        console.error('Failed to load games:', err);
        
        // Get a proper error message
        let errorMessage = 'Failed to load games';
        
        if (err.response?.data) {
          const data = err.response.data;
          if (typeof data === 'string') {
            errorMessage = data;
          } else if (data.error) {
            errorMessage = typeof data.error === 'string' ? data.error : JSON.stringify(data.error);
          } else if (data.message) {
            errorMessage = typeof data.message === 'string' ? data.message : JSON.stringify(data.message);
          } else if (typeof data === 'object') {
            // Try to extract a meaningful message from the object
            errorMessage = data.toString && typeof data.toString === 'function' && data.toString() !== '[object Object]'
              ? data.toString()
              : JSON.stringify(data);
          }
        } else if (err.message) {
          errorMessage = err.message;
        } else if (err.toString && err.toString() !== '[object Object]') {
          errorMessage = err.toString();
        }
        
        // Limit error message length for display
        if (errorMessage.length > 200) {
          errorMessage = errorMessage.substring(0, 200) + '...';
        }
        
        // Provide user-friendly error message
        let userMessage = 'Failed to load games';
        if (errorMessage.includes('Network Error') || errorMessage.includes('ECONNREFUSED')) {
          userMessage = 'Unable to connect to the server. Please check your connection.';
        } else if (errorMessage.includes('404') || errorMessage.includes('Not Found')) {
          userMessage = 'Games endpoint not found. Please contact support.';
        } else if (errorMessage.includes('500') || errorMessage.includes('Internal Server Error')) {
          userMessage = 'Server error occurred. Please try again later.';
        } else if (errorMessage !== 'Failed to load games') {
          // Only show technical error if it's meaningful
          userMessage = `Failed to load games. ${errorMessage}`;
        }
        
        setGames([]);
        console.error('Error details:', err);
        console.error('Error response:', err.response);
        alert(userMessage);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleGameClick = (game) => {
    if (game) navigate(createChallengePath(game));
  };

  const handleBackToGames = () => {
    navigate('/');
  };

  const scrollToGames = () => {
    const gamesSection = document.getElementById('games-section');
    if (gamesSection) {
      gamesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Slug URL: avoid flashing the marketing home while games load or before redirect for bad slug
  if (normalizedSlug) {
    if (loading || games.length === 0 || !selectedGame) {
      return (
        <div
          className="home-container"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '50vh',
          }}
        >
          <p style={{ color: '#aaa' }}>Loading…</p>
        </div>
      );
    }
  }

  // If a game is selected, show the form
  if (selectedGameId) {
    const gameTitle = selectedGame
      ? `Create ${selectedGame.name} challenge on EWIO`
      : 'Create a skill challenge on EWIO';
    const gameDescription = selectedGame
      ? `Create a ${selectedGame.name} challenge and bet with crypto. Challenge your friends, prove your skills, and earn real rewards. Secure escrow and automatic payouts.`
      : "Create a gaming challenge and bet with crypto. Challenge your friends and prove your skills.";
    
    return (
      <>
        <SEO
          title={gameTitle}
          description={gameDescription}
          structuredData={[
            organizationJsonLd(),
            {
              '@context': 'https://schema.org',
              '@type': 'WebApplication',
              name: 'EWIO challenge creator',
              applicationCategory: 'Game',
              operatingSystem: 'Web',
              offers: {
                '@type': 'Offer',
                price: '0',
                priceCurrency: 'USDT',
              },
              description: gameDescription,
              provider: { '@id': `${getSiteUrl()}/#organization` },
            },
          ]}
        />
        <div 
          className="home-container home-container-game-theme"
          style={{
            '--game-bg': getGameTheme(selectedGameId).background,
            '--game-header-bg': getGameTheme(selectedGameId).headerBg,
            '--game-accent': getGameTheme(selectedGameId).accent,
            '--game-back-button': getGameTheme(selectedGameId).backButton,
            '--game-back-button-hover': getGameTheme(selectedGameId).backButtonHover,
            '--game-title-gradient': getGameTheme(selectedGameId).titleGradient,
          }}
        >
          <div className="challenge-section">
            <div className="challenge-header">
              <button 
                className="back-button"
                onClick={handleBackToGames}
              >
                ← Back to Games
              </button>
              <h1 className="challenge-title">Create a New Challenge</h1>
            </div>
            <GameForm 
              selectedGameId={selectedGameId}
              games={games}
              onCancel={handleBackToGames} 
            />
          </div>
        </div>
      </>
    );
  }

  const homeStructuredData = [organizationJsonLd(), websiteJsonLd()];

  return (
    <>
      <SEO
        title={pageSettings.pageTitle}
        description={DEFAULT_SITE_META_DESCRIPTION}
        structuredData={homeStructuredData}
      />
      <div className="home-container">
      <div className="hero-section">
        <div className="hero-section__fallback" aria-hidden />
        {!prefersReducedMotion ? (
          <Vortex
            className="hero-section__vortex"
            transparentBackground
            particleCount={640}
            rangeY={120}
            baseHue={352}
            baseSpeed={0}
            rangeSpeed={1.35}
            baseRadius={0.8}
            rangeRadius={2.2}
          />
        ) : null}
        <div className="hero-content">
          <h1 className="hero-title">
            <Link
              to="/about"
              className="hero-title-link"
              aria-label={pageSettings.heroTitle}
            >
              {pageSettings.heroTitle}
            </Link>
          </h1>
          <p className="hero-subtitle">
            {prefersReducedMotion ? (
              HERO_ANIMATED_TAGLINE
            ) : (
              <TextGenerateEffect
                key={HERO_ANIMATED_TAGLINE}
                words={HERO_ANIMATED_TAGLINE}
                className="hero-subtitle-generate"
                duration={0.32}
                staggerDelay={0.04}
                filter
              />
            )}
          </p>
          <ShimmerCtaButton onClick={scrollToGames} className="hero-cta">
            New Challenge
          </ShimmerCtaButton>
        </div>
      </div>

      <div id="games-section" className="games-section">
        <div className="games-section-header">
          <h2 className="games-section-title">Available Games</h2>
        </div>

        {loading ? (
          <div className="games-loading">
            <div className="loading-spinner"></div>
            <p>Loading games...</p>
          </div>
        ) : games.length === 0 ? (
          <div className="no-games">
            <p>No games available. Please check your connection.</p>
          </div>
        ) : (
          <div className="games-grid">
            {games.map(game => (
              <GameCard
                key={game.gameId}
                game={game}
                onClick={() => handleGameClick(game)}
              />
            ))}
          </div>
        )}
      </div>

      <ChallengeRoadmap />

      <section className="how-it-works-section" aria-labelledby="how-ewio-works">
        <h2 id="how-ewio-works">
          <Link to="/about#how-ewio-works" className="how-it-works-link">How EWIO Works (smart‑contract wagering)</Link>
        </h2>
        <p>Bets are held in on-chain smart contracts. No custodial wallet—crypto stays in escrow until the challenge settles.</p>

        <h2>
          <Link to="/about#place-a-bet" className="how-it-works-link">Place a Bet Against Another Player</Link>
        </h2>
        <p>Choose a game, set your stake, and invite an opponent. Both players deposit into the contract; funds lock until results are verified.</p>

        <h2>
          <Link to="/about#settle-a-bet" className="how-it-works-link">Settle a Bet on‑Chain</Link>
        </h2>
        <p>Winners claim directly from the contract. Payouts are automatic—no manual approval, no withdrawal delays.</p>

        <h2>
          <Link to="/about#transparent-wagering" className="how-it-works-link">Transparent, Trustless Wagering</Link>
        </h2>
        <p>Every bet and settlement is recorded on-chain. No hidden fees, no third-party control over your funds.</p>
      </section>

      <section className="blog-teaser-section" aria-labelledby="blog-teaser-heading">
        <h2 id="blog-teaser-heading">Guides and blog</h2>
        <p>
          Long-form articles on smart-contract betting, getting started, and game-specific tips help you use EWIO with confidence.{' '}
          <Link to="/blog">Read the EWIO blog</Link>
          {' '}for tutorials and platform comparisons. See also{' '}
          <Link to="/spotlight">live featured challenges (Spotlight)</Link>,{' '}
          <Link to="/skill-based-wagering">skill-based crypto wagering</Link>
          {' '}and our <Link to="/press">press page</Link> for a concise product summary.
        </p>
      </section>

      <HomeExploreSections />
    </div>
    </>
  );
}
